import React, { useState } from "react";
import axios from "axios";
import config from "../config";
import OTPInput from "./OTPInput";
import { CHAT_LOCK_PIN_LENGTH } from "../utils/chatLock";
import "../styles/ChatLock.css";

function ChatLockPinResetModal({ user, onCancel, onSuccess }) {
  const [step, setStep] = useState("send");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const handleSendOtp = async () => {
    if (!user?.id) {
      setError("You must be logged in to reset your PIN.");
      return;
    }
    if (!user?.email) {
      setError("No email is linked to your account. Contact support.");
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const res = await axios.post(
        `${config.apiUrl}/api/auth/chat-lock-pin/send-reset-otp`,
        { userId: user.id }
      );
      setMaskedEmail(res.data.maskedEmail || "");
      setStep("verify");
      setInfo(res.data.message || "OTP sent to your email.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send OTP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async () => {
    setError("");
    if (emailOtp.length !== CHAT_LOCK_PIN_LENGTH) {
      setError("Enter the 4-digit OTP from your email.");
      return;
    }
    if (newPin.length !== CHAT_LOCK_PIN_LENGTH) {
      setError(`Enter a new ${CHAT_LOCK_PIN_LENGTH}-digit PIN.`);
      return;
    }
    if (newPin !== confirmPin) {
      setError("New PIN and confirmation do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${config.apiUrl}/api/auth/chat-lock-pin/verify-reset-otp`,
        { userId: user.id, otp: emailOtp }
      );
      onSuccess(newPin);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-lock-modal-backdrop" onClick={onCancel}>
      <div className="chat-lock-panel chat-lock-reset-panel" onClick={(e) => e.stopPropagation()}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#2563eb"
          strokeWidth="2"
          style={{ marginBottom: 12 }}
        >
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        <h3>Reset chat lock PIN</h3>

        {step === "send" ? (
          <>
            <p>
              We will send a one-time code to your registered email
              {user?.email ? ` (${user.email})` : ""} to verify it is you.
            </p>
            {error && <div className="chat-lock-error">{error}</div>}
            <div className="chat-lock-actions">
              <button type="button" className="chat-lock-btn secondary" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="chat-lock-btn primary"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? "Sending…" : "Send OTP"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p>
              Enter the OTP sent to <strong>{maskedEmail || "your email"}</strong>, then choose a new
              chat lock PIN.
            </p>
            {info && <p className="chat-lock-info">{info}</p>}
            <p className="chat-lock-field-label">Email OTP</p>
            <OTPInput value={emailOtp} onChange={setEmailOtp} length={CHAT_LOCK_PIN_LENGTH} />
            <p className="chat-lock-field-label">New PIN</p>
            <OTPInput value={newPin} onChange={setNewPin} length={CHAT_LOCK_PIN_LENGTH} masked />
            <p className="chat-lock-field-label">Confirm new PIN</p>
            <OTPInput value={confirmPin} onChange={setConfirmPin} length={CHAT_LOCK_PIN_LENGTH} masked />
            {error && <div className="chat-lock-error">{error}</div>}
            <div className="chat-lock-actions">
              <button
                type="button"
                className="chat-lock-btn secondary"
                onClick={() => {
                  setStep("send");
                  setEmailOtp("");
                  setNewPin("");
                  setConfirmPin("");
                  setError("");
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="button"
                className="chat-lock-btn link-style"
                onClick={handleSendOtp}
                disabled={loading}
              >
                Resend OTP
              </button>
              <button
                type="button"
                className="chat-lock-btn primary"
                onClick={handleResetPin}
                disabled={loading}
              >
                {loading ? "Verifying…" : "Reset PIN"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChatLockPinResetModal;
